import React, { useCallback, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { useDispatch, useSelector } from 'react-redux'

import { numberToDisplayString } from 'common/helpers/numberHelpers'
import useIsMobile from 'common/hooks/useIsMobile'
import { PreOfferService } from 'service/PreOfferService/PreOfferService'
import { AppDispatch, AppState } from 'store'
import { updateOffertOnCalendarSelectionChange } from 'store/ApplicationSlice/ApplicationSlice'
import { mapCurrentMonthStateToPreOffertModel } from 'store/InstalmentsMonthsSlice/instalmentsMonthHelperFunctions'
import { IUseSelectInstalmentsMonthsLogicProps, IuseChangeInstalmentsMenuLogicReturnProps, IuseSelectInstalmentsMonthsLogicReturnProps } from '../ChangeInstalmentsMenuProps'
import { changeInstalmentMonthSelection, iniciateInstalmentsMonthTable, quickMonthsChoiceSetActive } from 'store/InstalmentsMonthsSlice/InstalmentsMonthsSlice'

/**
 * Hook który na podstawie obecnego adresu url, ustala czy pokazywać stopkę i navbara na stronie czy nie
 */
export const useChangeInstalmentsMenuLogic = (): IuseChangeInstalmentsMenuLogicReturnProps => {
   const preOffertService = new PreOfferService()

   // >>> HOOKI
   const isMobileDevice = useIsMobile()
   const dispatch: AppDispatch = useDispatch()

   const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
   const [isLoading, setIsLoading] = useState<boolean>(false)
   const monthsInstalmentTable = useSelector((state: AppState) => state.instalmentsMonthsState)
   const applicationState = useSelector((state: AppState) => state.applicationState)

   // >>> ZMIENNE
   const currentMonthWithYear = DateTime.fromISO(monthsInstalmentTable[0]?.dateISO ??  "").toFormat('LLLL yyyy', { locale: 'pl' })
   const isCalendarValid = monthsInstalmentTable.every(singleMonth => singleMonth.hasError === false)
   const currentInstalmentsAmount = monthsInstalmentTable.filter(singleMonth => singleMonth.isSelected).length

   // >>> FUNKCJE POMOCNICZE
   const handleOnMenuClose = useCallback(() => setIsDialogOpen(false), [])
   const handleOnMenuOpen = useCallback(() => setIsDialogOpen(true), [])

   const getSelectInstalmentsMonthsHeading = (): string => {
      if(isCalendarValid) {
         return ` ${currentInstalmentsAmount} x ${numberToDisplayString(applicationState.offert.instalmentAmount, ' zł')}`
      }
      else {
         return ''
      }
   }
   
   // >>> FUNKCJE
   /**
    * Funkcja pozwalająca na przeliczenie na nowo parametrów oferty, po wyborze innego zestawu miesięcy.
    * Wysyła do serwisu informacje o aktualnym stanie kalendarza (które miesiące są zaznaczone a które nie), 
    * po czym aktualizuje ofertę na podstawie danych otrzymanych z Backendu po przeliczeniu. 
   */
   const handleRecalculateOffertOnMonthSelect = () => {
      if(isCalendarValid && monthsInstalmentTable.length !== 0) {
         const recalculatePreOfferParams = mapCurrentMonthStateToPreOffertModel(monthsInstalmentTable);
         
         (async () => {
            setIsLoading(true)
            const newOffert = await preOffertService.recalculatePreOffer(recalculatePreOfferParams)
            dispatch(updateOffertOnCalendarSelectionChange(newOffert))
            setIsLoading(false)
         })()
      }
   }

   return { 
      isMobileDevice,
      isDialogOpen,
      isLoading,
      applicationState,
      monthsInstalmentTable,

      calendarInfoParams: {
         currentMonthWithYear,
         currentInstalmentsAmount,
         isCalendarValid,
      } as const,

      dispatch,
      handleOnMenuClose,
      handleOnMenuOpen,
      getSelectInstalmentsMonthsHeading,
      handleRecalculateOffertOnMonthSelect,
   }
}

export const useSelectInstalmentsMonthsLogic = (props: IUseSelectInstalmentsMonthsLogicProps): IuseSelectInstalmentsMonthsLogicReturnProps => {

   const { monthsInstalmentTable } = props
   const [currentSelectedQuickButton, setCurrentSelectedQuickButton] = useState<number | undefined>(undefined)
   const paymentSchedule = useSelector((state: AppState) => state.applicationState.offert.paymentSchedule)

   const quickChoiceButtonsData: Record<string, number> = {
      TWO: 2,
      SIX: 6,
      ALL: monthsInstalmentTable.length
   }
   
   useEffect(() => { //Tworzymy tabelę z obiektami odpowiadającymi przyciską poszczególnych miesięcy
      props.dispatch(iniciateInstalmentsMonthTable(paymentSchedule))
   }, []) 

   useEffect(() => {
      props.handleRecalculateOffertOnMonthSelect()
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [monthsInstalmentTable])


   //Obsługa przycisku do szybkiego wybierania miesięcy
   const handleQuickChoiceButtonClick = (instalmentsNumber: number) => {
      if(currentSelectedQuickButton !== instalmentsNumber) { //Blokuje kilkukrotne klikanie w ten sam przycisk
         switch(instalmentsNumber){
            case quickChoiceButtonsData.TWO: {
               props.dispatch(quickMonthsChoiceSetActive(quickChoiceButtonsData.TWO))

               setCurrentSelectedQuickButton(quickChoiceButtonsData.TWO)
               break;
            }
            case quickChoiceButtonsData.SIX: {
               props.dispatch(quickMonthsChoiceSetActive(quickChoiceButtonsData.SIX))

               setCurrentSelectedQuickButton(quickChoiceButtonsData.SIX)
               break;
            }
            case quickChoiceButtonsData.ALL: {
               props.dispatch(quickMonthsChoiceSetActive(quickChoiceButtonsData.ALL))

               setCurrentSelectedQuickButton(quickChoiceButtonsData.ALL)
               break;
            }
         }
      }
   }

   // Obsługa pojedynczego przycisku z nazwą miesiąca
   const handleInstalmentsMonthButton = (currentSelectedMonthIndex: number, newSelectionValue: boolean) => {
      props.dispatch(changeInstalmentMonthSelection({ monthIndex: currentSelectedMonthIndex, newSelectionValue:  newSelectionValue }))
      setCurrentSelectedQuickButton(undefined)
   }

   
   return {
      quickChoiceButtonsData,
      handleQuickChoiceButtonClick,
      handleInstalmentsMonthButton
   }
}